import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Reveal, Kicker } from "./Reveal";
import { submitLead } from "@/lib/api";

const REVENUE = ["£50M–£150M", "£150M–£500M", "£500M–£1B", "£1B+"];
const POINTS = ["10k–50k", "50k–250k", "250k–500k", "500k+"];
const EMPTY = { name: "", email: "", company: "", revenue_band: "", price_points: "", message: "" };

const inputCls =
  "w-full border border-white/10 bg-[#0F0F11] px-5 py-4 font-mono text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-[#00FF41]";

export default function PilotCTA() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await submitLead(form);
      setDone(data.id.slice(0, 8).toUpperCase());
      toast.success("Application logged in the ledger.");
    } catch {
      toast.error("Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="pilot" className="border-b border-white/10" data-testid="pilot-section">
      <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-40">
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Kicker index="12" label="Design partners" />
            <h2 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="pilot-heading">
              Prove governed autonomy on <span className="text-[#00FF41]">real commercial decisions.</span>
            </h2>
            <p className="mt-8 max-w-md font-mono text-sm leading-relaxed text-zinc-400">
              Axiosera is selecting retail and e-commerce businesses for 90-day PriceStrike
              design-partner pilots. Each pilot is structured around:
            </p>
            <div className="mt-12 space-y-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
              <div>A defined decision scope</div>
              <div>Approved commercial policy</div>
              <div>Holdout or control measurement</div>
              <div>Progressive autonomy</div>
              <div>Measurable financial outcomes</div>
              <div className="text-[#00FF41]">Complete decision evidence</div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="scanlines border border-white/10 bg-[#0A0A0B]">
              <div className="flex items-center justify-between border-b border-white/10 px-8 py-4">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">design_partner_application.form</span>
                <span className="font-mono text-[11px] text-[#00FF41] blink" />
              </div>
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-10 font-mono text-xs leading-loose"
                    data-testid="pilot-success-message"
                  >
                    <div className="text-zinc-500">$ axiosera applications submit</div>
                    <div className="mt-4 text-white">status: <span className="text-[#00FF41]">LOGGED</span></div>
                    <div className="text-white">ledger entry: <span className="text-[#00FF41]">#{done}</span></div>
                    <div className="text-white">review window: <span className="text-[#00FF41]">48h</span></div>
                    <div className="mt-6 text-zinc-500">We reply from founder@axiosera.com. Governance starts with the first conversation.</div>
                  </motion.div>
                ) : (
                  <motion.form key="form" exit={{ opacity: 0 }} onSubmit={submit} className="grid gap-4 p-8 sm:grid-cols-2" data-testid="pilot-form">
                    <input required data-testid="pilot-name-input" placeholder="FULL NAME" value={form.name} onChange={set("name")} className={inputCls} />
                    <input required type="email" data-testid="pilot-email-input" placeholder="WORK EMAIL" value={form.email} onChange={set("email")} className={inputCls} />
                    <input required data-testid="pilot-company-input" placeholder="COMPANY" value={form.company} onChange={set("company")} className={`${inputCls} sm:col-span-2`} />
                    <select required data-testid="pilot-revenue-select" value={form.revenue_band} onChange={set("revenue_band")} className={`${inputCls} ${form.revenue_band ? "" : "text-zinc-600"}`}>
                      <option value="" disabled>REVENUE BAND</option>
                      {REVENUE.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <select required data-testid="pilot-pricepoints-select" value={form.price_points} onChange={set("price_points")} className={`${inputCls} ${form.price_points ? "" : "text-zinc-600"}`}>
                      <option value="" disabled>ACTIVE PRICE POINTS</option>
                      {POINTS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <textarea data-testid="pilot-message-input" placeholder="WHAT WOULD YOU DELEGATE FIRST?" rows={3} value={form.message} onChange={set("message")} className={`${inputCls} resize-none sm:col-span-2`} />
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="pilot-submit-button"
                      className="mt-2 bg-[#00FF41] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white disabled:opacity-50 sm:col-span-2"
                    >
                      {loading ? "Logging..." : "Submit application →"}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
